
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ZoomIn, ZoomOut, RotateCw, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import CountryFlag from "@/components/country/CountryFlag";
import { EditableDocumentMetadata } from "@/components/admin/identity-docs/table/preview-dialog/EditableDocumentMetadata";
import { DocumentPreviewActions } from "@/components/admin/identity-docs/table/preview-dialog/DocumentPreviewActions";
import { useSubmittedDocuments } from "@/hooks/admin/identity-docs/useSubmittedDocuments";
import { useDocumentViewHandler } from "@/components/admin/identity-docs/table/actions/useDocumentViewHandler";
import { useIsMobile } from "@/hooks/use-mobile";

const DocumentPreview = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [imageRotation, setImageRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isImage, setIsImage] = useState(false);

  // Fetch documents to find the specific document
  const { documents, isLoading: documentsLoading, refetch } = useSubmittedDocuments({
    search: "",
    status: "all",
    country: "all",
    groupByUser: false
  });

  const document = documents.find(doc => doc.id === documentId);
  
  const { handleViewDocument, loading, documentUrl, loadError } = useDocumentViewHandler({ 
    document: document! 
  });

  // Load document URL when component mounts
  useEffect(() => {
    if (document) {
      handleViewDocument();
    }
  }, [document]);

  // Reset image controls when document changes
  useEffect(() => {
    if (document) {
      setImageZoom(1);
      setImagePosition({ x: 0, y: 0 });
      setImageRotation(0);
    }
  }, [document?.id]);

  // Detect if the URL points to an image
  useEffect(() => {
    if (!documentUrl) {
      setIsImage(false);
      return;
    }

    const checkExtension = (url: string) => {
      const extension = url.split(/[?#]/)[0].split('.').pop()?.toLowerCase();
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
      return imageExtensions.includes(extension || '');
    };

    if (checkExtension(documentUrl) || checkExtension(document?.file_url || '')) {
      setIsImage(true);
      return;
    }

    const img = new Image();
    img.onload = () => setIsImage(true);
    img.onerror = () => setIsImage(false);
    img.src = documentUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [documentUrl, document?.file_url]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error("Error loading image:", e);
    e.currentTarget.src = "/placeholder.svg";
  };

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleRotateClockwise = () => {
    setImageRotation(prev => (prev + 90) % 360);
  };

  const handleRotateCounterClockwise = () => {
    setImageRotation(prev => (prev - 90 + 360) % 360);
  };

  const handleResetZoom = () => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
    setImageRotation(0);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && imageZoom > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDocumentUpdated = () => {
    refetch();
  };

  if (documentsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-blue mx-auto mb-4"></div>
          <p className="text-nation-blue">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center p-4">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4">Document Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm md:text-base">The document you're looking for could not be found.</p>
          <Button onClick={() => navigate("/admin/identity-docs")} size={isMobile ? "sm" : "default"}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Documents
          </Button>
        </div>
      </div>
    );
  }

  const shouldShowImageControls = !loading && documentUrl && isImage;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 md:px-6 py-3 md:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
            <Button 
              variant="outline" 
              onClick={() => navigate("/admin/identity-docs")}
              className="flex items-center gap-1 md:gap-2 flex-shrink-0"
              size={isMobile ? "sm" : "default"}
            >
              <ArrowLeft className="h-3 w-3 md:h-4 md:w-4" />
              <span className="hidden sm:inline">Back to Documents</span>
              <span className="sm:hidden">Back</span>
            </Button>
            
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
              <CountryFlag
                countryCode={document.profiles?.country_code}
                className="h-4 w-5 md:h-5 md:w-6 flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                  <span className="text-sm md:text-lg font-semibold truncate">
                    {document.id_document_types?.document_name} – {document.profiles?.country_code}
                  </span>
                  <Badge
                    variant={
                      document.verification_status === "verified"
                        ? "success"
                        : document.verification_status === "rejected"
                        ? "destructive"
                        : "outline"
                    }
                    className="text-xs flex-shrink-0"
                  >
                    {document.verification_status.charAt(0).toUpperCase() +
                      document.verification_status.slice(1)}
                  </Badge>
                </div>
                <div className="text-xs md:text-sm text-muted-foreground truncate">
                  Submitted by {document.profiles?.first_name} {document.profiles?.last_name}
                  {document.submitted_at && (
                    <span className="ml-1 md:ml-2">
                      on {format(new Date(document.submitted_at), "MMM d, yyyy")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`${
        isMobile ? 'flex flex-col' : 'flex gap-6'
      } p-3 md:p-6 ${
        isMobile ? 'min-h-[calc(100vh-80px)]' : 'h-[calc(100vh-120px)]'
      }`}>
        {/* Document Viewer */}
        <div className={`${
          isMobile ? 'w-full mb-4' : 'flex-1'
        } min-w-0 flex flex-col`}>
          {/* Image Controls */}
          {shouldShowImageControls && (
            <div className={`flex gap-1 md:gap-2 mb-3 md:mb-4 flex-shrink-0 bg-muted/30 p-2 md:p-3 rounded-lg border ${
              isMobile ? 'flex-wrap' : ''
            }`}>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleZoomOut} 
                disabled={imageZoom <= 0.25}
                className="flex items-center gap-1 text-xs md:text-sm"
              >
                <ZoomOut className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Zoom Out</span>
                <span className="sm:hidden">-</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleZoomIn} 
                disabled={imageZoom >= 3}
                className="flex items-center gap-1 text-xs md:text-sm"
              >
                <ZoomIn className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Zoom In</span>
                <span className="sm:hidden">+</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRotateCounterClockwise}
                className="flex items-center gap-1 text-xs md:text-sm"
              >
                <RotateCcw className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Rotate Left</span>
                <span className="sm:hidden">↺</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRotateClockwise}
                className="flex items-center gap-1 text-xs md:text-sm"
              >
                <RotateCw className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Rotate Right</span>
                <span className="sm:hidden">↻</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleResetZoom}
                className="flex items-center gap-1 text-xs md:text-sm"
              >
                Reset
              </Button>
              <div className={`flex items-center gap-2 text-xs md:text-sm text-muted-foreground bg-background px-2 md:px-3 py-1 rounded border ${
                isMobile ? 'w-full mt-2' : ''
              }`}>
                <span>Zoom: {Math.round(imageZoom * 100)}%</span>
                <span>•</span>
                <span>Rotation: {imageRotation}°</span>
              </div>
            </div>
          )}

          {/* Document Display */}
          <div className={`flex-1 border rounded-lg overflow-hidden bg-muted/30 relative ${
            isMobile ? 'min-h-[300px]' : ''
          }`}>
            {loading && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-t-2 border-b-2 border-nation-blue mx-auto mb-4"></div>
                  <p className="text-muted-foreground text-sm md:text-base">Loading document...</p>
                </div>
              </div>
            )}

            {!loading && documentUrl && !loadError && (
              <>
                {isImage ? (
                  <div 
                    className="w-full h-full overflow-hidden relative flex items-center justify-center"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{ cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
                  >
                    <img
                      src={documentUrl}
                      alt="Document"
                      className="max-w-full max-h-full object-contain"
                      style={{
                        transform: `scale(${imageZoom}) translate(${imagePosition.x / imageZoom}px, ${imagePosition.y / imageZoom}px) rotate(${imageRotation}deg)`,
                        transformOrigin: 'center',
                      }}
                      onError={handleImageError}
                      draggable={false}
                    />
                  </div>
                ) : (
                  <object
                    data={documentUrl}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    className="w-full h-full"
                  >
                    <p className="p-4 text-sm md:text-base">
                      Your browser does not support embedded PDFs. <br />
                      <a href={documentUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Download the document.
                      </a>
                    </p>
                  </object>
                )}
              </>
            )}

            {!loading && loadError && (
              <div className="flex flex-col items-center justify-center h-full gap-4 p-4">
                <p className="text-destructive font-medium text-sm md:text-base">{loadError}</p>
                <div className="text-xs md:text-sm text-muted-foreground text-center">
                  <p>Document path: {document.file_url}</p>
                  <p className="mt-1">
                    Please check that the file exists in the storage bucket and has the correct permissions.
                  </p>
                </div>
              </div>
            )}

            {!loading && !documentUrl && !loadError && (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground text-sm md:text-base">Failed to load document URL</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className={`${
          isMobile ? 'w-full' : 'w-96'
        } flex-shrink-0`}>
          <ScrollArea className={isMobile ? 'h-auto' : 'h-full'}>
            <div className="space-y-4 md:space-y-6 p-1">
              {/* Document Actions */}
              <DocumentPreviewActions 
                document={document} 
                onDocumentUpdated={handleDocumentUpdated}
              />

              {/* Document Metadata */}
              <div className="flex-1">
                <EditableDocumentMetadata 
                  document={document} 
                  onDocumentUpdated={handleDocumentUpdated}
                />
              </div>

              {/* Verification Information */}
              <div className="border rounded-lg p-3 md:p-4">
                <h4 className="text-sm font-medium mb-3">Verification Status</h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium">Status:</span>
                    <div className="mt-1">
                      <Badge
                        variant={
                          document.verification_status === "verified"
                            ? "success"
                            : document.verification_status === "rejected"
                            ? "destructive"
                            : "outline"
                        }
                        className="text-xs"
                      >
                        {document.verification_status.charAt(0).toUpperCase() +
                          document.verification_status.slice(1)}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">Purpose:</span>
                    <p className="text-muted-foreground capitalize">verification</p>
                  </div>
                  {document.primary_document && (
                    <div>
                      <Badge variant="secondary" className="text-xs">Primary Document</Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
